import { type FC, useEffect, useState } from 'react';
import { Input } from 'antd';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import { TPO_SAVE_AS_PLAN_MODAL, TPO_SAVE_AS_PLAN_NAME_INPUT } from '../../../config/testIds';
import { useTpoGenConfig } from '../../../context/CustomerConfigContext';
import useSavePlanAsMutation, { type SavePlanAsResponse } from '../hooks/mutations/useSavePlanAsMutation';

export interface SaveAsPlanModalProps {
    open: boolean;
    onClose: () => void;
    planId: string;
    planName: string;
    onSuccess: (data: SavePlanAsResponse, planName: string) => void;
}

const SaveAsPlanModal: FC<SaveAsPlanModalProps> = ({ open, onClose, planId, planName, onSuccess }) => {
    const { labels } = useTpoGenConfig();
    const [name, setName] = useState('');

    const { mutate: savePlanAs, isPending } = useSavePlanAsMutation({
        onSuccess: (data, payload) => {
            onClose();
            onSuccess(data, payload.planName);
        },
    });

    useEffect(() => {
        if (!open) return;
        setName(`${planName}_copy`);
    }, [open, planName]);

    const trimmedName = name.trim();
    const question = labels.modals.saveAsPlanQuestion.replace('{planName}', planName);

    const description = (
        <div className="bg-[#fafafa] rounded-lg p-2 flex flex-col gap-y-2">
            <p className="text-sm text-[#18181b]">{question}</p>
            <label className="mt-2 flex flex-col gap-y-1 text-[#18181B]">
                <span>
                    <span className="text-red-500">*</span> {labels.modals.planNameLabel}
                </span>
                <Input
                    data-testid={TPO_SAVE_AS_PLAN_NAME_INPUT}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isPending}
                />
            </label>
        </div>
    );

    return (
        <ConfirmationModal
            data-testid={TPO_SAVE_AS_PLAN_MODAL}
            open={open}
            onCancel={onClose}
            onSubmitClick={() => savePlanAs({ planId, planName: trimmedName })}
            okText="Save"
            okButtonProps={{
                type: 'primary',
                loading: isPending,
                disabled: !trimmedName,
                style: { borderRadius: 6 },
            }}
            loading={isPending}
            title={labels.modals.saveAsPlanTitle}
            description={description}
            width={520}
        />
    );
};

SaveAsPlanModal.displayName = 'SaveAsPlanModal';
export default SaveAsPlanModal;
